"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import EditMaterialOrderView from "./EditMaterialOrderView";
import { NumericFormat } from "react-number-format";
import AlertDialog from "./AlertDialog";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #4b5563",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
};

export default function MaterialOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    purchaseOrder: "",
    vendor: "",
    poNumber: "",
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    attachments: [],
    materialOrderNumber: "",
    contact: [],
    fieldContact: [],
    deliveryContact: null,
    expectedDeliveryDate: "",
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [project, setProject] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sizeFilter, setSizeFilter] = useState(null);
  const [bidAreaFilter, setBidAreaFilter] = useState(null);
  const [error, setError] = useState(null);
  const [showOrders, setShowOrders] = useState(false);
  const [viewOrder, setViewOrder] = useState(location.state?.order || null);
  const [totalCost, setTotalCost] = useState(0);
  const userRole = localStorage.getItem("userRole");
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const [
          projectResponse,
          vendorsResponse,
          contactsResponse,
          usersResponse,
          materialOrdersResponse,
        ] = await Promise.all([
          fetch(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`Project fetch failed: ${res.status} - ${text}`);
            }
            return res.json();
          }),
          fetch(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`Vendors fetch failed: ${res.status} - ${text}`);
            }
            return res.json();
          }),
          fetch(
            "https://erp-production-72da01c8e651.herokuapp.com/api/contacts",
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`Contacts fetch failed: ${res.status} - ${text}`);
            }
            return res.json();
          }),
          fetch("https://erp-production-72da01c8e651.herokuapp.com/api/users", {
            headers: { Authorization: `Bearer ${token}` },
          }).then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`Users fetch failed: ${res.status} - ${text}`);
            }
            return res.json();
          }),
          fetch(
            `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/project/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(async (res) => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(
                `Material orders fetch failed: ${res.status} - ${text}`
              );
            }
            return res.json();
          }),
        ]);

        setProject(projectResponse);
        setVendors(vendorsResponse);
        setContacts(contactsResponse);
        setUsers(usersResponse);
        setMaterialOrders(materialOrdersResponse);
      } catch (err) {
        setError("Failed to load data: " + (err.message || "Unknown error"));
        console.error("Fetch error:", err);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const updateFilteredItems = () => {
      if (!project || !formData.purchaseOrder) {
        setFilteredItems([]);
        console.log("No project or purchaseOrder selected:", {
          project: !!project,
          purchaseOrder: formData.purchaseOrder,
        });
        return;
      }

      const selectedPO = project.purchaseOrders.find(
        (po) => po._id === formData.purchaseOrder
      );
      if (!selectedPO) {
        setFilteredItems([]);
        console.log("Selected PO not found:", formData.purchaseOrder);
        return;
      }

      const items = selectedPO.lineItems
        .filter((lineItem) => {
          const vendorId = formData.vendor || lineItem.vendor;
          const projectItem =
            project.projectItems.find(
              (pi) => pi._id === lineItem.projectItem
            ) || {};
          const matchesSearch = searchQuery
            ? (lineItem.title || projectItem.title || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              (lineItem.itemDescription || projectItem.description || "")
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
            : true;
          const matchesSize = sizeFilter
            ? (lineItem.size || projectItem.size || "N/A") === sizeFilter
            : true;
          const matchesBidArea = bidAreaFilter
            ? lineItem.bidArea === bidAreaFilter
            : true;
          const available =
            (lineItem.requiredQty || lineItem.quantity || 0) -
            (projectItem.quantityOrdered || 0) -
            (projectItem.quantityPending || 0);
          return (
            vendorId &&
            lineItem.vendor === vendorId &&
            matchesSearch &&
            matchesSize &&
            matchesBidArea &&
            available > 0
          );
        })
        .map((lineItem) => {
          const projectItem =
            project.projectItems.find(
              (pi) => pi._id === lineItem.projectItem
            ) || {};
          return {
            _id: lineItem._id,
            projectItemId: lineItem.projectItem,
            title: lineItem.title || projectItem.title || "Untitled",
            description:
              lineItem.itemDescription || projectItem.description || "",
            size: lineItem.size || projectItem.size || "N/A",
            quantityRequired: lineItem.requiredQty || lineItem.quantity || 0,
            quantityOrdered: projectItem.quantityOrdered || 0,
            quantityPending: projectItem.quantityPending || 0,
            container: lineItem.container || projectItem.container || "N/A",
            bidArea: lineItem.bidArea || projectItem.bidArea || "(unassigned)",
            itemType: "material",
            vendor: lineItem.vendor,
            unitCost: lineItem.unitCost || projectItem.cost || 0,
          };
        });

      setFilteredItems(items);
      console.log("Filtered items updated:", items);
    };

    updateFilteredItems();
  }, [
    project,
    formData.purchaseOrder,
    formData.vendor,
    searchQuery,
    sizeFilter,
    bidAreaFilter,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleContactChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      contact: selected ? selected.map((option) => option.value) : [],
    }));
  };

  const handleFieldContactChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      fieldContact: selected ? selected.map((option) => option.value) : [],
    }));
  };

  const handleDeliveryContactChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      deliveryContact: selected ? selected.value : null,
    }));
  };

  const fetchNextMaterialOrderNumber = async (poNumber) => {
    const token = localStorage.getItem("token");
    const response = await fetch(
      "https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/next-number",
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to fetch material order number: ${response.status} - ${text}`
      );
    }
    const { nextNumber } = await response.json();
    return `${poNumber}-${nextNumber}`;
  };

  const handlePOChange = (selected) => {
    const poId = selected ? selected.value : "";

    if (selectedItems.length > 0) {
      setDialog({
        open: true,
        type: "warning",
        title: "Confirm Purchase Order Change",
        message:
          "Changing the Purchase Order will clear all selected items. Do you want to proceed?",
        onConfirm: () => {
          proceedWithPOChange(poId);
          setDialog({ ...dialog, open: false });
        },
        onCancel: () => {
          setDialog({ ...dialog, open: false });
        },
      });
    } else {
      proceedWithPOChange(poId);
    }
  };

  const proceedWithPOChange = async (poId) => {
    const token = localStorage.getItem("token");
    let po = null;

    try {
      const projectResponse = await fetch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!projectResponse.ok) {
        const text = await projectResponse.text();
        throw new Error(
          `Project fetch failed: ${projectResponse.status} - ${text}`
        );
      }
      const updatedProject = await projectResponse.json();
      setProject(updatedProject);

      po = updatedProject.purchaseOrders.find((p) => p._id === poId);
      const materialOrderNumber = poId
        ? await fetchNextMaterialOrderNumber(po.poNumber.toString())
        : "";

      setFormData({
        purchaseOrder: poId,
        poNumber: po ? po.poNumber.toString() : "",
        vendor: "",
        materialOrderNumber,
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        attachments: [],
        contact: [],
        fieldContact: [],
        deliveryContact: null,
        expectedDeliveryDate: "",
      });
      setSelectedItems([]);
      setError(null);
    } catch (err) {
      setError("Failed to handle PO change: " + err.message);
      console.error("PO change error:", err);

      if (project) {
        po = project.purchaseOrders.find((p) => p._id === poId);
      }

      setFormData({
        purchaseOrder: poId,
        poNumber: po ? po.poNumber.toString() : "",
        vendor: "",
        materialOrderNumber: po ? `${po.poNumber}-N/A` : "",
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        attachments: [],
        contact: [],
        fieldContact: [],
        deliveryContact: null,
        expectedDeliveryDate: "",
      });
      setSelectedItems([]);
    }
  };

  const handleVendorChange = (selected) => {
    setFormData({
      ...formData,
      vendor: selected ? selected.value : "",
      contact: [],
    });
  };

  const handleItemSelect = (itemId) => {
    const item = filteredItems.find((i) => i._id === itemId);
    if (!item) return;

    setSelectedItems((prev) => {
      if (prev.some((i) => i.itemId === itemId)) {
        const newSelectedItems = prev.filter((i) => i.itemId !== itemId);
        const newVendor =
          newSelectedItems.length > 0
            ? filteredItems.find((i) => i._id === newSelectedItems[0].itemId)
                .vendor
            : ""; // Reset vendor to empty string if no items are selected
        setFormData((prevForm) => ({
          ...prevForm,
          vendor: newVendor,
          contact: [],
        }));
        return newSelectedItems;
      }

      if (prev.length > 0) {
        const currentVendor = filteredItems.find(
          (i) => i._id === prev[0].itemId
        ).vendor;
        if (item.vendor && item.vendor !== currentVendor) {
          setDialog({
            open: true,
            type: "error",
            title: "Vendor Mismatch",
            message:
              "All items must be from the same vendor. Please create a separate order for items from different vendors.",
            onConfirm: () => setDialog({ ...dialog, open: false }),
          });
          return prev;
        }
      }

      const newSelectedItems = [
        ...prev,
        {
          itemId: itemId,
          quantity: 0,
          size: item.size || "N/A",
          bidArea: item.bidArea || "(unassigned)",
          price: item.unitCost || 0,
        },
      ];

      if (!formData.vendor || prev.length === 0) {
        setFormData((prevForm) => ({
          ...prevForm,
          vendor: item.vendor || "",
          contact: [],
        }));
      }

      return newSelectedItems;
    });
  };

  const getUserEmailFromToken = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.email;
      } catch (err) {
        console.error("Error decoding token for email:", err);
        return null;
      }
    }
    return null;
  };

  const handleQuantityChange = (itemId, value) => {
    const item = filteredItems.find((i) => i._id === itemId);
    const maxOrderable =
      item.quantityRequired - item.quantityOrdered - item.quantityPending;
    const quantity = Math.min(Math.max(0, parseInt(value) || 0), maxOrderable);
    setSelectedItems((prev) =>
      prev.map((i) => (i.itemId === itemId ? { ...i, quantity } : i))
    );
  };

  const handlePriceChange = (itemId, value) => {
    const price = parseFloat(value) || 0;
    setSelectedItems((prev) =>
      prev.map((i) => (i.itemId === itemId ? { ...i, price } : i))
    );
  };

  const handleBidAreaChange = (itemId, selected) => {
    setSelectedItems((prev) =>
      prev.map((i) =>
        i.itemId === itemId
          ? { ...i, bidArea: selected ? selected.value : "" }
          : i
      )
    );
  };

  useEffect(() => {
    const total = selectedItems.reduce((sum, item) => {
      const lineItem = filteredItems.find((i) => i._id === item.itemId);
      return sum + item.quantity * (item.price || lineItem?.unitCost || 0);
    }, 0);
    setTotalCost(total);
  }, [selectedItems, filteredItems]);

  const handleSubmit = async () => {
    if (
      !formData.purchaseOrder ||
      !formData.vendor ||
      !formData.poNumber ||
      !formData.materialOrderNumber ||
      selectedItems.length === 0
    ) {
      setError(
        "PO, Vendor, PO Number, Material Order Number, and at least one item are required."
      );
      return;
    }
    if (selectedItems.some((i) => i.quantity <= 0)) {
      setError("All selected items must have a quantity greater than 0.");
      return;
    }
    if (selectedItems.some((i) => !i.bidArea)) {
      setError("All selected items must have a bid area.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const orderData = {
        project: id,
        purchaseOrder: formData.purchaseOrder,
        vendor: formData.vendor,
        poNumber: formData.poNumber,
        materialOrderNumber: formData.materialOrderNumber,
        items: JSON.stringify(
          selectedItems.map((i) => ({
            projectItem: filteredItems.find((fi) => fi._id === i.itemId)
              .projectItemId,
            quantity: i.quantity,
            bidArea: i.bidArea,
            size: i.size,
            price: i.price,
          }))
        ),
        shippingMethod: formData.shippingMethod,
        deliveryInstructions: formData.deliveryInstructions,
        userEmail: getUserEmailFromToken(),
        contact: JSON.stringify(formData.contact),
        fieldContact: JSON.stringify(formData.fieldContact),
        deliveryContact: formData.deliveryContact || null,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
      };

      console.log("Sending order data:", orderData);

      const formDataToSend = new FormData();
      for (const [key, value] of Object.entries(orderData)) {
        if (value !== null && value !== undefined) {
          formDataToSend.append(key, value);
        }
      }

      const response = await fetch(
        "https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${text}`);
      }
      const { materialOrder, project: updatedProject } = await response.json();

      setProject(updatedProject);
      setMaterialOrders((prev) => [...prev, materialOrder]);
      setSelectedItems([]);
      setFormData({
        purchaseOrder: "",
        vendor: "",
        poNumber: "",
        shippingMethod: "Delivery",
        deliveryInstructions: "",
        attachments: [],
        materialOrderNumber: "",
        contact: [],
        fieldContact: [],
        deliveryContact: null,
        expectedDeliveryDate: "",
      });
      navigate(`/Projects/${id}`, {
        state: { initialTab: "Materials" },
      });
    } catch (err) {
      setError("Failed to create material order: " + err.message);
      console.error("Submit error:", err);
    }
  };

  const showConfirmationDialog = (e) => {
    e.preventDefault();
    setDialog({
      open: true,
      type: "success",
      title: "Confirm Material Order",
      message: "Are you sure you want to create this material order?",
      onConfirm: () => {
        handleSubmit();
        setDialog({ ...dialog, open: false });
      },
    });
  };

  const closeDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  const handleViewOrder = (order) => {
    navigate(`/projects/${id}/material-order/${order._id}`);
  };

  const poOptions =
    project?.purchaseOrders?.map((po) => ({
      value: po._id,
      label: `${po.title} (PO# ${po.poNumber})`,
    })) || [];

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName || "Unnamed Vendor",
  }));

  const contactOptions = contacts
    .filter((contact) => {
      const vendorId = formData.vendor;
      return vendorId ? contact.relatedAccount === vendorId : true;
    })
    .map((contact) => ({
      value: contact._id,
      label: `${contact.firstName} ${contact.lastName} (${
        contact.email || "No email"
      })`,
    }));

  const userOptions = users.map((user) => ({
    value: user._id,
    label: `${user.displayName || "Unnamed User"} (${
      user.phoneNumber || "No phone"
    })`,
  }));

  const sizeOptions = useMemo(() => {
    return [...new Set(filteredItems.map((item) => item.size || "N/A"))].map(
      (size) => ({ value: size, label: size })
    );
  }, [filteredItems]);

  const bidAreaOptions = useMemo(() => {
    return [
      ...new Set(filteredItems.map((item) => item.bidArea || "(unassigned)")),
    ].map((area) => ({ value: area, label: area }));
  }, [filteredItems]);

  if (!project)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-700">{error}</div>
    );

  return (
    <div className="ml-4 mx-auto px-4 py-6">
      <AlertDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={closeDialog}
        onConfirm={dialog.onConfirm}
      />
      {viewOrder ? (
        <EditMaterialOrderView
          order={viewOrder}
          project={project}
          vendors={vendors}
          materialOrders={materialOrders}
          setMaterialOrders={setMaterialOrders}
          setViewOrder={setViewOrder}
          navigate={navigate}
        />
      ) : (
        <>
          <div className="mb-6 shadow-lg bg-gray-700 rounded-lg p-6 border border-gray-600">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-white">
                Create Material Order
              </h1>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowOrders(!showOrders)}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                >
                  {showOrders ? "Hide Orders" : "View Orders"}
                </button>
                <button
                  onClick={() =>
                    navigate(`/Projects/${project._id}`, {
                      state: { initialTab: "Materials" },
                    })
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Back
                </button>
              </div>
            </div>
          </div>

          {!showOrders ? (
            <form onSubmit={showConfirmationDialog} className="space-y-6">
              <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Order Details
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Purchase Order
                    </label>
                    <Select
                      options={poOptions}
                      value={
                        poOptions.find(
                          (option) => option.value === formData.purchaseOrder
                        ) || null
                      }
                      onChange={handlePOChange}
                      placeholder="Select Purchase Order"
                      className="mt-1"
                      styles={customStyles}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Vendor
                    </label>
                    <Select
                      options={vendorOptions}
                      value={
                        vendorOptions.find(
                          (option) => option.value === formData.vendor
                        ) || null
                      }
                      onChange={handleVendorChange}
                      placeholder="Vendor"
                      className="mt-1"
                      styles={customStyles}
                      required
                      isDisabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      PO Number
                    </label>
                    <input
                      type="text"
                      name="poNumber"
                      value={formData.poNumber}
                      onChange={handleChange}
                      placeholder="Enter PO Number"
                      className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      readOnly
                      required
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Material Order Number
                    </label>
                    <input
                      type="text"
                      name="materialOrderNumber"
                      value={formData.materialOrderNumber}
                      readOnly
                      className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Shipping Method
                    </label>
                    <select
                      name="shippingMethod"
                      value={formData.shippingMethod}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    >
                      <option value="Delivery">Delivery</option>
                      <option value="Pickup">Pickup</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Expected Delivery Date
                    </label>
                    <input
                      type="date"
                      name="expectedDeliveryDate"
                      value={formData.expectedDeliveryDate}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Contacts
                    </label>
                    <Select
                      options={contactOptions}
                      value={contactOptions.filter((option) =>
                        formData.contact.includes(option.value)
                      )}
                      onChange={handleContactChange}
                      placeholder="Select Contacts"
                      className="mt-1"
                      styles={customStyles}
                      isMulti
                      isDisabled={!formData.vendor}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Field Contacts
                    </label>
                    <Select
                      options={userOptions}
                      value={userOptions.filter((option) =>
                        formData.fieldContact.includes(option.value)
                      )}
                      onChange={handleFieldContactChange}
                      placeholder="Select Field Contacts"
                      className="mt-1"
                      styles={customStyles}
                      isMulti
                    />
                  </div>
                  {formData.shippingMethod === "Pickup" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Delivery Driver Contact
                      </label>
                      <Select
                        options={userOptions}
                        value={
                          userOptions.find(
                            (option) =>
                              option.value === formData.deliveryContact
                          ) || null
                        }
                        onChange={handleDeliveryContactChange}
                        placeholder="Select Delivery Contact"
                        className="mt-1"
                        styles={customStyles}
                        isClearable
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Delivery Instructions
                  </label>
                  <textarea
                    name="deliveryInstructions"
                    value={formData.deliveryInstructions}
                    onChange={handleChange}
                    placeholder="Enter delivery instructions"
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-white">
                    Select Materials to Order
                  </h2>
                  {formData.purchaseOrder && (
                    <div className="flex flex-wrap items-center space-x-4">
                      <div className="w-48">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search title or description"
                          className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div className="w-48">
                        <Select
                          key={`size-filter-${sizeFilter || "null"}`}
                          options={sizeOptions}
                          value={
                            sizeOptions.find(
                              (option) => option.value === sizeFilter
                            ) || null
                          }
                          onChange={(selected) =>
                            setSizeFilter(selected ? selected.value : null)
                          }
                          placeholder="Filter by Size"
                          styles={customStyles}
                          isClearable
                        />
                      </div>
                      <div className="w-48">
                        <Select
                          key={`bid-area-filter-${bidAreaFilter || "null"}`}
                          options={bidAreaOptions}
                          value={
                            bidAreaOptions.find(
                              (option) => option.value === bidAreaFilter
                            ) || null
                          }
                          onChange={(selected) =>
                            setBidAreaFilter(selected ? selected.value : null)
                          }
                          placeholder="Filter by Bid Area"
                          styles={customStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            setSearchQuery("");
                            setSizeFilter(null);
                            setBidAreaFilter(null);
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {filteredItems.length === 0 ? (
                  <p className="text-gray-400">
                    No materials available to order or select a PO to see items.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-800">
                            Select
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Vendor
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Qty Req
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Qty Ord
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Qty Pend
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Qty to Order
                          </th>
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Price
                            </th>
                          )}
                          {userRole === "foreman" ? (
                            ""
                          ) : (
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Line Item Total
                            </th>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Container
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                            Bid Area
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-600">
                        {filteredItems.map((item) => {
                          const selectedItem = selectedItems.find(
                            (i) => i.itemId === item._id
                          );
                          const maxOrderable =
                            item.quantityRequired -
                            item.quantityOrdered -
                            item.quantityPending;
                          const vendor = vendors.find(
                            (v) => v._id === item.vendor
                          );
                          const lineItemTotal = selectedItem
                            ? (
                                selectedItem.quantity * selectedItem.price
                              ).toFixed(2)
                            : "0.00";
                          return (
                            <tr
                              key={item._id}
                              className={
                                selectedItem
                                  ? "bg-orange-600/20"
                                  : "hover:bg-gray-500 transition-colors duration-200"
                              }
                            >
                              <td
                                className={
                                  selectedItem
                                    ? "px-2 py-2 whitespace-nowrap text-sm text-gray-200 sticky left-0 bg-orange-600/20"
                                    : "px-2 py-2 whitespace-nowrap text-sm text-gray-200 sticky left-0 bg-gray-700"
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={!!selectedItem}
                                  onChange={() => handleItemSelect(item._id)}
                                  className="h-5 w-5 text-gray-700 focus:ring-orange-500 border-gray-500 rounded"
                                />
                              </td>
                              <td
                                className="px-2 py-2 text-sm text-gray-200 max-w-[300px] truncate"
                                title={item.description || ""}
                              >
                                {item.description.length > 50
                                  ? `${item.description.substring(0, 120)}...`
                                  : item.description}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {item.size}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {vendor?.companyName || "Unknown"}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {item.quantityRequired}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {item.quantityOrdered}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {item.quantityPending}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                <input
                                  type="number"
                                  value={
                                    selectedItem ? selectedItem.quantity : ""
                                  }
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item._id,
                                      e.target.value
                                    )
                                  }
                                  disabled={!selectedItem}
                                  min="0"
                                  max={maxOrderable}
                                  className="w-20 rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                                />
                              </td>
                              {userRole === "foreman" ? (
                                ""
                              ) : (
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                  <NumericFormat
                                    value={
                                      selectedItem
                                        ? selectedItem.price
                                        : item.unitCost
                                    }
                                    displayType="text"
                                    thousandSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                  />
                                </td>
                              )}
                              {userRole === "foreman" ? (
                                ""
                              ) : (
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                  <NumericFormat
                                    value={lineItemTotal}
                                    displayType="text"
                                    thousandSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                  />
                                </td>
                              )}
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                                {item.container || "N/A"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                                <Select
                                  options={bidAreaOptions}
                                  value={
                                    bidAreaOptions.find(
                                      (option) =>
                                        option.value ===
                                        (selectedItem
                                          ? selectedItem.bidArea
                                          : item.bidArea)
                                    ) || null
                                  }
                                  onChange={(selected) =>
                                    handleBidAreaChange(item._id, selected)
                                  }
                                  placeholder="Select Bid Area"
                                  styles={customStyles}
                                  isDisabled={!selectedItem}
                                  className="w-40"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {userRole === "foreman" ? (
                  ""
                ) : (
                  <div className="mt-4">
                    <p className="text-lg font-semibold text-gray-200">
                      Total Cost:{" "}
                      <NumericFormat
                        value={totalCost}
                        displayType="text"
                        thousandSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                      />
                    </p>
                  </div>
                )}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedItems([]);
                      setFormData({
                        purchaseOrder: "",
                        vendor: "",
                        poNumber: "",
                        shippingMethod: "Delivery",
                        deliveryInstructions: "",
                        attachments: [],
                        materialOrderNumber: "",
                        contact: [],
                        fieldContact: [],
                        deliveryContact: null,
                        expectedDeliveryDate: "",
                      });
                      navigate(`/Projects/${id}`, {
                        state: { initialTab: "Materials" },
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  >
                    Create Order
                  </button>
                </div>
              </div>

              {error && <p className="text-red-400 mb-2">{error}</p>}
            </form>
          ) : (
            <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
              <h2 className="text-lg font-semibold text-orange-400 mb-4">
                Material Orders
              </h2>
              {materialOrders.length === 0 ? (
                <p className="text-gray-400">No material orders found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Material Order #
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          PO Number
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Shipping Method
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Contacts
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Field Contacts
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Delivery Contact
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Expected Delivery Date
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {materialOrders.map((order) => {
                        const vendor = vendors.find(
                          (v) => v._id === (order.vendor?._id || order.vendor)
                        );
                        const contactsText =
                          order.contact
                            .map((contactId) => {
                              const contact = contacts.find(
                                (c) => c._id === contactId
                              );
                              return contact
                                ? `${contact.firstName} ${contact.lastName} (${
                                    contact.email || "No email"
                                  })`
                                : "Unknown";
                            })
                            .join(", ") || "N/A";
                        const fieldContactsText =
                          order.fieldContact
                            .map((userId) => {
                              const user = users.find((u) => u._id === userId);
                              return user
                                ? `${user.displayName} (${
                                    user.phoneNumber || "No phone"
                                  })`
                                : "Unknown";
                            })
                            .join(", ") || "N/A";
                        const deliveryContactText = order.deliveryContact
                          ? users.find((u) => u._id === order.deliveryContact)
                            ? `${
                                users.find(
                                  (u) => u._id === order.deliveryContact
                                ).displayName
                              } (${
                                users.find(
                                  (u) => u._id === order.deliveryContact
                                ).phoneNumber || "No phone"
                              })`
                            : "Unknown"
                          : "N/A";

                        return (
                          <tr
                            key={order._id}
                            className="hover:bg-gray-500 transition-colors duration-200"
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {order.materialOrderNumber}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {order.poNumber}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {vendor?.companyName || "Unknown"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {order.shippingMethod}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  order.status === "pending"
                                    ? "bg-yellow-600/20 text-yellow-400"
                                    : order.status === "completed"
                                    ? "bg-green-600/20 text-green-400"
                                    : "bg-red-600/20 text-red-400"
                                }`}
                              >
                                {order.status.charAt(0).toUpperCase() +
                                  order.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {contactsText}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {fieldContactsText}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {order.shippingMethod === "Pickup"
                                ? deliveryContactText
                                : "N/A"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {formatDateForInput(order.expectedDeliveryDate)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                              <button
                                onClick={() => handleViewOrder(order)}
                                className="text-orange-400 hover:text-orange-300 mr-2"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
